import React, { useState, useRef, useContext, useEffect } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import ConfigurableValues from '../../config/constants'
import { getRestaurantDetail, createFood, editFood } from '../../apollo'
import AddonComponent from '../Addon/Addon'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Typography,
  Input,
  Alert,
  Modal,
  Button,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  useTheme,
  FormControl,
  Switch,
  IconButton
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import FormCardHeader from '../FormCardHeader'
import ConfigurationContext from '../../context/Configuration'
import { selectMatchInputSx } from '../Dropdown'
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined'
import InputAdornment from '@mui/material/InputAdornment'
import CloseRounded from '@mui/icons-material/CloseRounded'

const MAX_GALLERY_PHOTOS = 8
const CREATE_FOOD = gql`
  ${createFood}
`
const EDIT_FOOD = gql`
  ${editFood}
`
const GET_CATEGORIES = gql`
  ${getRestaurantDetail}
`
const GET_ADDONS = gql`
  ${getRestaurantDetail}
`

// Quick-pick emojis shown next to the product name in the customer app
const EMOJI_SUGGESTIONS = [
  '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  '🥓',
  '🍳',
  '🌭',
  '🥐',
  '🍞',
  '🥪',
  '🍔',
  '🍟',
  '🍕',
  '🌮',
  '🍗',
  '🥩',
  '🐟',
  '🍤',
  '🍚',
  '🍜',
  '🥗',
  '🍰',
  '🍪',
  '🍦',
  '☕',
  '🥤',
  '🍺',
  '🔥',
  '🌶️',
  '🌱',
  '⭐'
]

// Keeps a single emoji (some emojis are several code points, e.g. 🌶️ or the
// Scottish flag, which is a base flag plus tag characters)
const takeFirstEmoji = value => {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  const graphemes = Array.from(trimmed)
  let result = graphemes[0]
  for (let i = 1; i < graphemes.length; i++) {
    const char = graphemes[i]
    const code = char.codePointAt(0)
    const isModifier =
      code === 0xfe0f || // variation selector
      code === 0x200d || // zero width joiner
      (code >= 0x1f3fb && code <= 0x1f3ff) || // skin tones
      code === 0x20e3 || // combining enclosing keycap
      (code >= 0x1f1e6 && code <= 0x1f1ff) || // regional indicators (country flags)
      (code >= 0xe0020 && code <= 0xe007f) // tag characters (e.g. 🏴󠁧󠁢󠁳󠁣󠁴󠁿)
    const previousCode = graphemes[i - 1].codePointAt(0)
    if (isModifier || previousCode === 0x200d) {
      result += char
    } else {
      break
    }
  }
  return result
}

// Min/Max are how many of the addon's options the customer picks for this
// item; spell that out instead of printing the raw numbers.
const addonPickRule = (addon, t) => {
  const min = Number(addon.quantityMinimum) || 0
  const max = Number(addon.quantityMaximum) || 0
  if (min === max) {
    return min === 0
      ? t('AddonPickOptional')
      : t('AddonPickExactly', { count: min })
  }
  if (min === 0) return t('AddonPickUpTo', { count: max })
  return t('AddonPickRange', { min, max })
}

function Food(props) {
  console.log('🚀 ~ Food ~ props:', props.food)
  const theme = useTheme()

  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const formRef = useRef()
  const mutation = props.food ? EDIT_FOOD : CREATE_FOOD
  const [title, setTitle] = useState(props.food ? props.food.title : '')
  const [emoji, setEmoji] = useState(props.food ? props.food.emoji || '' : '')
  const [description, setDescription] = useState(
    props.food ? props.food.description : ''
  )
  // Kept apart from `description`: the store page shows the short line above
  // the price and this longer block under it, one feature per line.
  const [details, setDetails] = useState(
    props.food ? props.food.details || '' : ''
  )
  const [category, setCategory] = useState(
    props.food ? props.food.categoryId : ''
  )
  const [imgMenu, imgMenuSetter] = useState(props.food ? props.food.image : '')
  const [previewBroken, setPreviewBroken] = useState(false)
  // Extra photos (saved URLs or freshly picked data URLs) shown as a gallery
  // in the app. Only offered to Fashion & Beauty stores.
  const [gallery, setGallery] = useState(
    props.food && props.food.images ? [...props.food.images] : []
  )
  // A new pick gets a fresh chance to load
  useEffect(() => setPreviewBroken(false), [imgMenu])
  const [variationIndex, variationIndexSetter] = useState(0)
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [titleError, titleErrorSetter] = useState(null)
  const [categoryError, categoryErrorSetter] = useState(null)
  const [imageError, imageErrorSetter] = useState(null)
  const [addonModal, addonModalSetter] = useState(false)
  const [variation, variationSetter] = useState(
    props.food
      ? props.food.variations.map(
          ({ title, price, discounted, addons, isOutOfStock }) => {
            return {
              title,
              price,
              discounted,
              addons,
              isOutOfStock: Boolean(isOutOfStock),
              titleError: null,
              priceError: null
            }
          }
        )
      : [
          {
            title: '',
            price: '',
            discounted: '',
            addons: [],
            isOutOfStock: false,
            titleError: null,
            priceError: null
          }
        ]
  )

  const restaurantId = localStorage.getItem('restaurantId')

  const clearFields = () => {
    // formRef.current.reset()
    variationSetter([
      {
        title: '',
        price: '',
        discounted: '',
        addons: [],
        titleError: null,
        priceError: null,
        isOutOfStock: false
      }
    ])
    imgMenuSetter('')
    setGallery([])
    setDetails('')
    titleErrorSetter(null)
    categoryErrorSetter(null)
    imageErrorSetter(null)
  }

  const onDismiss = () => {
    successSetter('')
    mainErrorSetter('')
  }

  const onError = error => {
    mainErrorSetter(`${t('ActionFailedTryAgain')} ${error}`)
    successSetter('')
    setTimeout(onDismiss, 3000)
  }
  const onCompleted = data => {
    if (!props.food) clearFields()
    const message = props.food
      ? t('FoodUpdatedSuccessfully')
      : t('FoodAddedSuccessfully')
    mainErrorSetter('')
    successSetter(message)
    // Only the add form empties itself for the next entry. Doing this while
    // editing blanked the fields of the record still on screen, and the modal
    // stays open for a few seconds after saving.
    if (!props.food) {
      setTitle('')
      setDescription('')
      setDetails('')
      setEmoji('')
    }
    setTimeout(onDismiss, 3000)
  }
  const [mutate, { loading: mutateLoading }] = useMutation(mutation, {
    onError,
    onCompleted,
    // Delete and the out-of-stock toggle already refresh the list this way;
    // create and edit did not, so the table kept rendering the values it was
    // first given — most visibly a replaced image, which stayed on the old
    // picture until the page was reloaded.
    refetchQueries: [
      { query: GET_CATEGORIES, variables: { id: restaurantId } }
    ],
    awaitRefetchQueries: true
  })

  const {
    data: dataCategories,
    error: errorCategories,
    loading: loadingCategories
  } = useQuery(GET_CATEGORIES, {
    variables: {
      id: restaurantId
    }
  })
  // Store Type (or, for older stores, the shop type) names the tile, e.g.
  // "Fashion & Beauty" / "fashion-beauty"
  const storeInfo = dataCategories?.restaurant
  const isFashionStore = /fashion/i.test(
    `${storeInfo?.categoryType || ''} ${storeInfo?.shopType || ''}`
  )

  const {
    data: dataAddons,
    error: errorAddons,
    loading: loadingAddons
  } = useQuery(GET_ADDONS, {
    variables: {
      id: restaurantId
    }
  })

  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }
  // Case-insensitive, and matching the formats the other admin uploaders
  // accept: a phone photo arrives as IMG_1234.JPG and a modern export as .webp,
  // both of which a lowercase-only jpg|jpeg|png|gif test threw away silently.
  const IMAGE_NAME_PATTERN = /\.(jpg|jpeg|png|gif|webp|svg)$/i

  const filterImage = event => {
    let images = []
    for (var i = 0; i < event.target.files.length; i++) {
      images[i] = event.target.files.item(i)
    }
    images = images.filter(image => IMAGE_NAME_PATTERN.test(image.name))
    return images.length ? images[0] : undefined
  }

  const imageToBase64 = imgUrl => {
    const fileReader = new FileReader()
    fileReader.onloadend = () => {
      imgMenuSetter(fileReader.result)
    }
    fileReader.onerror = () => {
      mainErrorSetter(t('ImageReadFailed'))
    }
    fileReader.readAsDataURL(imgUrl)
  }

  const selectImage = event => {
    const picked = event.target.files?.length
    const result = filterImage(event)
    if (result) {
      mainErrorSetter('')
      imageErrorSetter(true)
      imageToBase64(result)
      return
    }
    // Say so rather than leaving the form looking like the click did nothing.
    if (picked) mainErrorSetter(t('ImageTypeNotSupported'))
  }

  const onAdd = index => {
    const variations = variation
    if (index === variations.length - 1) {
      variations.push({
        title: '',
        price: '',
        discounted: '',
        addons: [],
        titleError: null,
        priceError: null
      })
    } else {
      variations.splice(index + 1, 0, {
        title: '',
        price: '',
        discounted: '',
        addons: [],
        titleError: null,
        priceError: null
      })
    }
    variationSetter([...variations])
  }
  const onRemove = index => {
    if (variation.length === 1 && index === 0) {
      return
    }
    const variations = variation
    variations.splice(index, 1)
    variationSetter([...variations])
  }
  const handleVariationChange = (event, index, type) => {
    const variations = [...variation]

    if (type === 'title') {
      variations[index][type] =
        event.target.value.length === 1
          ? event.target.value.toUpperCase()
          : event.target.value
      variationSetter([...variations])
    } else if (type === 'isOutOfStock') {
      variations[index][type] = event.target.checked
      variationSetter([...variations])
    } else {
      variations[index][type] = event.target.value
      variationSetter([...variations])
    }
  }
  const onSubmitValidaiton = () => {
    const titleError = !validateFunc(
      { title: formRef.current['input-title'].value },
      'title'
    )
    const categoryError = !validateFunc(
      { category: formRef.current['input-category'].value },
      'category'
    )
    const variations = variation
    variations.map(variationItem => {
      variationItem.priceError = !validateFunc(
        { price: variationItem.price },
        'price'
      )
      let error = false
      const occ = variation.filter(v => v.title === variationItem.title)
      if (occ.length > 1) error = true
      variationItem.titleError = error
        ? !error
        : variations.length > 1
        ? !validateFunc({ title: variationItem.title }, 'title')
        : true

      return variationItem
    })
    const variationsError = !variation.filter(
      variationItem => !variationItem.priceError || !variationItem.titleError
    ).length
    titleErrorSetter(titleError)
    categoryErrorSetter(categoryError)
    variationSetter([...variations])
    return titleError && categoryError && variationsError
  }

  const onBlurVariation = (index, type) => {
    const variations = [...variation]
    if (type === 'title') {
      const occ = variations.filter(v => v.title === variations[index][type])
      if (occ.length > 1) {
        variations[index][type + 'Error'] = false
      } else {
        variations[index][type + 'Error'] =
          variations.length > 1
            ? !validateFunc({ [type]: variations[index][type] }, type)
            : true
      }
    }

    if (type === 'price') {
      variations[index][type + 'Error'] = !validateFunc(
        { [type]: variations[index][type] },
        type
      )
    }
    variationSetter([...variations])
  }

  const updateAddonsList = ids => {
    const variations = variation
    variations[variationIndex].addons = variations[
      variationIndex
    ].addons.concat(ids)
    variationSetter([...variations])
  }

  // show Create Addon modal
  const toggleModal = index => {
    addonModalSetter(prev => !prev)
    variationIndexSetter(index)
  }
  const onSelectAddon = (index, id) => {
    const variations = variation
    const addon = variations[index].addons.indexOf(id)
    if (addon < 0) variations[index].addons.push(id)
    else variations[index].addons.splice(addon, 1)
    variationSetter([...variations])
  }

  // Throws on failure rather than returning undefined: a swallowed error here
  // fell back to the existing image, so the form reported success while the
  // picture stayed exactly as it was.
  const uploadImageToCloudinary = async () => {
    if (imgMenu === '') return ''
    if (props.food && props.food.image === imgMenu) return imgMenu
    return uploadFile(imgMenu)
  }

  // Already uploaded photos pass through; picked files (data URLs) upload
  const uploadGallery = () =>
    Promise.all(
      gallery.map(photo =>
        photo.startsWith('data:') ? uploadFile(photo) : photo
      )
    )

  const selectGalleryImages = event => {
    const files = Array.from(event.target.files || []).filter(file =>
      IMAGE_NAME_PATTERN.test(file.name)
    )
    if (!files.length) {
      if (event.target.files?.length)
        mainErrorSetter(t('ImageTypeNotSupported'))
      return
    }
    const room = MAX_GALLERY_PHOTOS - gallery.length
    if (files.length > room) {
      mainErrorSetter(t('GalleryLimit', { count: MAX_GALLERY_PHOTOS }))
    }
    files.slice(0, Math.max(0, room)).forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () =>
        setGallery(current => [...current, reader.result])
      reader.onerror = () => mainErrorSetter(t('ImageReadFailed'))
      reader.readAsDataURL(file)
    })
  }

  const uploadFile = async file => {
    if (!CLOUDINARY_UPLOAD_URL || !CLOUDINARY_FOOD) {
      throw new Error(t('ImageUploadNotConfigured'))
    }

    const apiUrl = `${CLOUDINARY_UPLOAD_URL.replace(/\/$/, '')}/image/upload`
    const data = {
      file,
      upload_preset: CLOUDINARY_FOOD
    }
    const result = await fetch(apiUrl, {
      body: JSON.stringify(data),
      headers: {
        'content-type': 'application/json'
      },
      method: 'POST'
    })
    const imageData = await result.json()
    if (!result.ok || !imageData?.secure_url) {
      throw new Error(imageData?.error?.message || t('ImageUploadFailed'))
    }
    return imageData.secure_url
  }
  const { t } = props
  const configuration = useContext(ConfigurationContext)
  const currencyUnit =
    configuration?.currencySymbol || configuration?.currency || ''
  const currencyAdornment = currencyUnit ? (
    <InputAdornment position="start">
      <Typography
        component="span"
        sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
        {currencyUnit}
      </Typography>
    </InputAdornment>
  ) : null
  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  return (
    <Box container className={[classes.container, classes.width60]}>
      <FormCardHeader
        className={classes.heading}
        title={props.food ? t('Edit Food') : t('Add Food')}
        onClose={props.onClose}
      />
      <Box className={classes.form}>
        <form ref={formRef}>
          <Box>
            <Typography className={classes.labelText}>{t('Title')}</Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-title"
              name="input-title"
              placeholder={t('Title')}
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={event =>
                onBlur(titleErrorSetter, 'title', event.target.value)
              }
              disableUnderline
              className={[
                globalClasses.input,
                titleError === false
                  ? globalClasses.inputError
                  : titleError === true
                  ? globalClasses.inputSuccess
                  : ''
              ]}
            />
            <Typography className={classes.labelText}>
              {t('Emoji')} ({t('Optional')})
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-food-emoji"
              name="input-food-emoji"
              placeholder={t('PHFoodEmoji')}
              type="text"
              value={emoji}
              onChange={e => setEmoji(takeFirstEmoji(e.target.value))}
              disableUnderline
              className={globalClasses.input}
            />
            <Box
              mt={1}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                alignItems: 'center'
              }}>
              {EMOJI_SUGGESTIONS.map(suggestion => (
                <Button
                  key={suggestion}
                  type="button"
                  onClick={() =>
                    setEmoji(current =>
                      current === suggestion ? '' : suggestion
                    )
                  }
                  style={{
                    minWidth: 36,
                    height: 36,
                    padding: 0,
                    fontSize: 18,
                    lineHeight: '36px',
                    borderRadius: 8,
                    border:
                      emoji === suggestion
                        ? '2px solid #F6985D'
                        : '1px solid #E0E0E0',
                    backgroundColor:
                      emoji === suggestion ? '#FFF3E9' : 'transparent'
                  }}>
                  {suggestion}
                </Button>
              ))}
            </Box>
            <Typography
              className={classes.labelText}
              style={{ marginTop: 8, marginBottom: 8, opacity: 0.7 }}>
              {t('Preview')}:{' '}
              {emoji ? `${title || t('Title')} ${emoji}` : title || t('Title')}
            </Typography>
            <Typography className={classes.labelText}>
              {t('Description')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-description"
              name="input-description"
              placeholder={t('Description')}
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disableUnderline
              className={[globalClasses.input]}
            />
            <Typography className={classes.labelText}>
              {t('ProductDetails')} ({t('Optional')})
            </Typography>
            <Input
              id="input-details"
              name="input-details"
              placeholder={t('PHProductDetails')}
              multiline
              minRows={4}
              maxRows={10}
              value={details}
              onChange={e => setDetails(e.target.value)}
              disableUnderline
              className={globalClasses.textArea}
            />
            <Typography
              className={classes.labelText}
              style={{ opacity: 0.7, fontSize: 13, marginTop: 6 }}>
              {t('ProductDetailsHelp')}
            </Typography>
            {loadingCategories && <div>Loading...</div>}
            {errorCategories && <div>Error {errorCategories.message}</div>}

            <Typography className={classes.labelText}>
              {t('Category')}
            </Typography>
            <Box className={globalClasses.flexRow}>
              <Select
                id="input-category"
                name="input-category"
                defaultValue={[category || '']}
                value={category}
                onChange={e => setCategory(e.target.value)}
                onBlur={event =>
                  onBlur(categoryErrorSetter, 'category', event.target.value)
                }
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                sx={selectMatchInputSx}
                className={[
                  globalClasses.input,
                  categoryError === false
                    ? globalClasses.inputError
                    : categoryError === true
                    ? globalClasses.inputSuccess
                    : ''
                ]}>
                {!category && (
                  <MenuItem value="" style={{ color: 'black' }}>
                    {t('SelectCategory')}
                  </MenuItem>
                )}
                {dataCategories &&
                  dataCategories.restaurant.categories.map(category => (
                    <MenuItem
                      value={category._id}
                      key={category._id}
                      style={{ color: 'black' }}>
                      {category.title}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
            <Box
              mt={3}
              mb={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5
              }}>
              {(imgMenu || props.food?.image) && !previewBroken ? (
                <img
                  className={classes.image}
                  alt={t('Image')}
                  style={
                    imageError === false ? { border: '1px solid #d32f2f' } : {}
                  }
                  src={imgMenu || props.food.image}
                  // A dead saved URL falls back to the empty tile rather than
                  // the browser's broken-image glyph
                  onError={() => setPreviewBroken(true)}
                />
              ) : (
                // No photo yet: a clear empty tile instead of the browser's
                // broken-image glyph.
                <Box
                  className={classes.imagePlaceholder}
                  sx={imageError === false ? { borderColor: '#d32f2f' } : {}}>
                  <AddPhotoAlternateOutlined sx={{ fontSize: 30 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                    {t('NoImageYet')}
                  </Typography>
                </Box>
              )}
              <label
                htmlFor={props.food ? 'edit-food-image' : 'add-food-image'}
                className={classes.fileUpload}>
                {t('UploadAnImage')}
              </label>
              <input
                className={classes.file}
                id={props.food ? 'edit-food-image' : 'add-food-image'}
                type="file"
                accept="image/*"
                onChange={event => {
                  selectImage(event)
                  // Allow re-picking the same file after a failed attempt;
                  // without this the input holds the value and fires nothing.
                  event.target.value = ''
                }}
              />
            </Box>

            {isFashionStore && (
              <Box mb={3}>
                <Typography className={classes.labelText}>
                  {t('MorePhotos')} ({gallery.length}/{MAX_GALLERY_PHOTOS})
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mt: 1
                  }}>
                  {gallery.map((photo, index) => (
                    <Box
                      key={`${index}-${photo.slice(-24)}`}
                      sx={{
                        position: 'relative',
                        width: 88,
                        height: 88,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid #E9EBF0'
                      }}>
                      <img
                        src={photo}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        size="small"
                        aria-label={t('Remove')}
                        onClick={() =>
                          setGallery(current =>
                            current.filter((_, i) => i !== index)
                          )
                        }
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          p: '2px',
                          bgcolor: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' }
                        }}>
                        <CloseRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                  {gallery.length < MAX_GALLERY_PHOTOS && (
                    <Box
                      component="label"
                      htmlFor={
                        props.food ? 'edit-food-gallery' : 'add-food-gallery'
                      }
                      className={classes.imagePlaceholder}
                      sx={{
                        width: 88,
                        height: 88,
                        cursor: 'pointer',
                        m: 0
                      }}>
                      <AddPhotoAlternateOutlined sx={{ fontSize: 24 }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                        {t('AddPhotos')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  className={classes.file}
                  id={props.food ? 'edit-food-gallery' : 'add-food-gallery'}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={event => {
                    selectGalleryImages(event)
                    event.target.value = ''
                  }}
                />
                <Typography
                  className={classes.labelText}
                  style={{ opacity: 0.7, fontSize: 13 }}>
                  {t('MorePhotosHelp')}
                </Typography>
              </Box>
            )}

            <Box className={classes.container}>
              <Box className={classes.flexRow}>
                <Box item className={classes.heading}>
                  <Typography variant="p" className={classes.textWhite}>
                    {t('Variations')}
                  </Typography>
                </Box>
              </Box>
              <Box classes={classes.form}>
                {variation.map((variationItem, index) => (
                  <Box key={variationItem._id} pl={1} pr={1}>
                    <Box className={globalClasses.flexRow}>
                      <Grid container columnSpacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box mt={2}>
                            <Typography className={classes.labelText}>
                              {t('UniqueTitle')}
                            </Typography>
                            <Input
                              style={{ marginTop: -1 }}
                              id="input-type"
                              placeholder={t('Title')}
                              type="text"
                              value={variationItem.title}
                              onChange={event => {
                                handleVariationChange(
                                  event,
                                  index,
                                  'title',
                                  'variations'
                                )
                              }}
                              onBlur={event => {
                                onBlurVariation(index, 'title')
                              }}
                              disableUnderline
                              className={[
                                globalClasses.input,
                                variationItem.titleError === false
                                  ? globalClasses.inputError
                                  : variationItem.titleError === true
                                  ? globalClasses.inputSuccess
                                  : ''
                              ]}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box mt={2}>
                            <Typography className={classes.labelText}>
                              {t('Price')}
                            </Typography>
                            <Input
                              style={{ marginTop: -1 }}
                              value={variationItem.price}
                              id="input-price"
                              placeholder={t('Price')}
                              type="number"
                              onChange={event => {
                                handleVariationChange(
                                  event,
                                  index,
                                  'price',
                                  'variations'
                                )
                              }}
                              onBlur={event => {
                                onBlurVariation(index, 'price')
                              }}
                              startAdornment={currencyAdornment}
                              disableUnderline
                              className={[
                                globalClasses.input,
                                variationItem.priceError === false
                                  ? globalClasses.inputError
                                  : variationItem.priceError === true
                                  ? globalClasses.inputSuccess
                                  : ''
                              ]}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box mt={2}>
                            <Typography className={classes.labelText}>
                              {t('Discounted')}
                            </Typography>
                            <Input
                              style={{ marginTop: -1 }}
                              value={variationItem.discounted}
                              id="input-discounted"
                              placeholder={t('Discounted')}
                              type="number"
                              onChange={event => {
                                handleVariationChange(
                                  event,
                                  index,
                                  'discounted',
                                  'variations'
                                )
                              }}
                              onBlur={event => {
                                onBlurVariation(index, 'discounted')
                              }}
                              startAdornment={currencyAdornment}
                              disableUnderline
                              className={[globalClasses.input]}
                            />
                            <Typography
                              className={classes.labelText}
                              style={{
                                opacity: 0.7,
                                fontSize: 12,
                                marginTop: 6
                              }}>
                              {Number(variationItem.discounted) > 0 &&
                              Number(variationItem.price) > 0
                                ? t('DiscountPreview', {
                                    was: `${currencyUnit}${(
                                      Number(variationItem.price) +
                                      Number(variationItem.discounted)
                                    ).toFixed(2)}`,
                                    now: `${currencyUnit}${Number(
                                      variationItem.price
                                    ).toFixed(2)}`,
                                    interpolation: { escapeValue: false }
                                  })
                                : t('DiscountedHelp')}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box mt={2}>
                            <Typography className={classes.labelText}>
                              {t('Out of Stock')}
                            </Typography>
                            <FormControl>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="large"
                                    checked={variationItem.isOutOfStock}
                                    onChange={event =>
                                      handleVariationChange(
                                        event,
                                        index,
                                        'isOutOfStock'
                                      )
                                    }
                                    color="primary"
                                  />
                                }
                              />
                            </FormControl>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <RemoveIcon
                        style={{
                          backgroundColor: theme.palette.common.black,
                          color: theme.palette.warning.dark,
                          borderRadius: '50%',
                          marginTop: 12,
                          marginRight: 10
                        }}
                        onClick={() => {
                          onRemove(index)
                        }}
                      />
                      <AddIcon
                        style={{
                          backgroundColor: theme.palette.warning.dark,
                          color: theme.palette.common.white,
                          borderRadius: '50%',
                          marginTop: 12
                        }}
                        onClick={() => {
                          onAdd(index)
                        }}
                      />
                    </Box>
                    <Box>
                      {loadingAddons && t('LoadingDots')}
                      {errorAddons && t('ErrorDots')}
                      {dataAddons &&
                        dataAddons.restaurant.addons.map(addon => (
                          <Grid
                            item
                            xs={12}
                            md={6}
                            key={addon._id}
                            style={{ textAlign: 'left', paddingLeft: 20 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  value={addon._id}
                                  checked={variation[index].addons.includes(
                                    addon._id
                                  )}
                                  onChange={() =>
                                    onSelectAddon(index, addon._id)
                                  }
                                />
                              }
                              label={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography
                                    sx={{ fontSize: 14, fontWeight: 600 }}>
                                    {addon.title}
                                  </Typography>
                                  {addon.description ? (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: 'text.secondary'
                                      }}>
                                      {addon.description}
                                    </Typography>
                                  ) : null}
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: 'text.disabled'
                                    }}>
                                    {addonPickRule(addon, t)}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', mr: 0 }}
                            />
                          </Grid>
                        ))}
                    </Box>
                    <Button
                      className={classes.button}
                      onClick={() => toggleModal(index)}>
                      {t('NewAddon')}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box>
            <Button
              className={globalClasses.button}
              disabled={mutateLoading}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidaiton() && !mutateLoading) {
                  let finalImage
                  let finalGallery = []
                  try {
                    finalImage = await uploadImageToCloudinary()
                    if (isFashionStore) finalGallery = await uploadGallery()
                  } catch (uploadError) {
                    // Stop here: saving now would quietly keep the old picture.
                    mainErrorSetter(uploadError.message)
                    successSetter('')
                    return
                  }
                  mutate({
                    variables: {
                      foodInput: {
                        restaurant: restaurantId,
                        _id: props.food ? props.food._id : '',
                        title: formRef.current['input-title'].value,
                        emoji,
                        description: formRef.current['input-description'].value,
                        details,
                        image: finalImage,
                        // Other stores never see the gallery, so leave any
                        // saved photos alone rather than wiping them
                        images: isFashionStore
                          ? finalGallery
                          : props.food?.images || [],
                        category: formRef.current['input-category'].value,
                        variations: variation.map(
                          ({
                            title,
                            price,
                            discounted,
                            addons,
                            isOutOfStock
                          }) => {
                            return {
                              title,
                              price: +price,
                              discounted: +discounted,
                              addons,
                              isOutOfStock
                            }
                          }
                        )
                      }
                    }
                  })
                  // Close the modal after 3 seconds by calling the parent's onClose callback
                  setTimeout(() => {
                    props.onClose && props.onClose() // Close the modal
                  }, 4000)
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {success && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {success}
              </Alert>
            )}
            {mainError && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {mainError}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
      <Modal
        style={{
          // width: '75%',
          marginLeft: '25%',
          overflowY: 'auto'
        }}
        open={addonModal}
        onClose={() => {
          toggleModal()
        }}>
        <AddonComponent updateAddonsList={updateAddonsList} />
      </Modal>
    </Box>
  )
}
export default withTranslation()(Food)
